import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const adminApi = () => axios.create({
  baseURL: API,
  headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
});

type Tab = 'overview' | 'users' | 'funnel' | 'revenue' | 'email' | 'errors';

const SIDEBAR_ITEMS: { id: Tab; icon: string; label: string }[] = [
  { id: 'overview', icon: '📊', label: 'Overview'     },
  { id: 'users',    icon: '👥', label: 'Users'        },
  { id: 'funnel',   icon: '💳', label: 'Funnel'       },
  { id: 'revenue',  icon: '💰', label: 'Revenue'      },
  { id: 'email',    icon: '📧', label: 'Email Blast'  },
  { id: 'errors',   icon: '🚨', label: 'Auth Errors'  },
];

const EVENT_COLORS: any = {
  pricing_page_visit: '#6366f1', payment_initiated: '#f59e0b',
  payment_success: '#10b981', payment_failed: '#ef4444', payment_cancelled: '#9ca3af',
};

const TYPE_COLORS: any = {
  login_failed: '#f59e0b', google_auth_failed: '#6366f1',
  register_failed: '#a855f7', otp_failed: '#10b981',
};

const StatCard = ({ label, value, sub, color, icon }: any) => (
  <div style={{
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
    padding: '20px 24px', flex: 1, minWidth: 150,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: '#6b7280', letterSpacing: 1, fontWeight: 600, textTransform: 'uppercase' }}>{label}</div>
      {icon && <div style={{ fontSize: 20 }}>{icon}</div>}
    </div>
    <div style={{ fontSize: 30, fontWeight: 800, color: color || '#111', lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>{sub}</div>}
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab]           = useState<Tab>('overview');
  const [stats, setStats]       = useState<any>(null);
  const [users, setUsers]       = useState<any[]>([]);
  const [funnel, setFunnel]     = useState<any[]>([]);
  const [revenue, setRevenue]   = useState<any>(null);
  const [search, setSearch]     = useState('');
  const [page, setPage]         = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]   = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userLinks, setUserLinks]       = useState<any[]>([]);
  const [userAnalytics, setUserAnalytics] = useState<any>(null);
  const [userModalTab, setUserModalTab]   = useState<'links' | 'analytics'>('links');
  const [emailForm, setEmailForm] = useState({ subject: '', body: '', target: 'all' });
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult]   = useState('');
  const [authErrors, setAuthErrors]     = useState<any[]>([]);
  const [authSummary, setAuthSummary]   = useState<any[]>([]);
  const [errorFilter, setErrorFilter]   = useState('');

  const logout = useCallback(() => {
    localStorage.removeItem('adminToken');
    navigate('/adminUser/login');
  }, [navigate]);

  const fetchStats = useCallback(async () => {
    try { const res = await adminApi().get('/adminUser/stats'); setStats(res.data); }
    catch { logout(); }
  }, [logout]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await adminApi().get(`/adminUser/users?page=${page}&search=${search}`);
      setUsers(res.data.users); setTotalPages(res.data.pages);
    } catch {}
  }, [page, search]);

  const fetchFunnel   = useCallback(async () => { try { const r = await adminApi().get('/adminUser/funnel-events'); setFunnel(r.data); } catch {} }, []);
  const fetchRevenue  = useCallback(async () => { try { const r = await adminApi().get('/adminUser/revenue'); setRevenue(r.data); } catch {} }, []);

  useEffect(() => {
    if (!localStorage.getItem('adminToken')) { navigate('/adminUser/login'); return; }
    fetchStats().finally(() => setLoading(false));
  }, [navigate, fetchStats]);

  useEffect(() => { if (tab === 'users')   fetchUsers();   }, [tab, page, search, fetchUsers]);
  useEffect(() => { if (tab === 'funnel')  fetchFunnel();  }, [tab, fetchFunnel]);
  useEffect(() => { if (tab === 'revenue') fetchRevenue(); }, [tab, fetchRevenue]);
  useEffect(() => {
    if (tab === 'errors') {
      adminApi().get(`/adminUser/auth-errors${errorFilter ? `?type=${errorFilter}` : ''}`)
        .then(res => { setAuthErrors(res.data.errors); setAuthSummary(res.data.summary); })
        .catch(() => {});
    }
  }, [tab, errorFilter]);

  const showMsg = (msg: string) => { setActionMsg(msg); setTimeout(() => setActionMsg(''), 3000); };

  const togglePremium = async (userId: string) => {
    try { const r = await adminApi().patch(`/adminUser/users/${userId}/toggle-premium`); showMsg(r.data.message); setUsers(u => u.map(x => x._id === userId ? { ...x, isPremium: !x.isPremium } : x)); }
    catch { showMsg('Error!'); }
  };

  const toggleBan = async (userId: string) => {
    try { const r = await adminApi().patch(`/adminUser/users/${userId}/toggle-ban`); showMsg(r.data.message); setUsers(u => u.map(x => x._id === userId ? { ...x, isBanned: !x.isBanned } : x)); }
    catch { showMsg('Error!'); }
  };

  const deleteUser = async (userId: string, name: string) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try { await adminApi().delete(`/adminUser/users/${userId}`); showMsg('User deleted!'); setUsers(u => u.filter(x => x._id !== userId)); }
    catch { showMsg('Error!'); }
  };

  const sendTrialMail = async (userId: string, email: string) => {
    try {
      await adminApi().post(`/adminUser/users/${userId}/send-trial-mail`);
      showMsg(`✅ Trial expire email sent to ${email}`);
    } catch { showMsg('❌ Email send failed'); }
  };

  const isTrialExpired = (user: any) => {
    if (user.isPremium) return false;
    if (!user.proTrialStart) return false;
    const trialEnd = new Date(user.proTrialStart);
    trialEnd.setDate(trialEnd.getDate() + 7);
    return new Date() > trialEnd;
  };

  const openUserModal = async (user: any) => {
    setSelectedUser(user); setUserModalTab('links');
    try {
      const [lr, ar] = await Promise.all([adminApi().get(`/adminUser/users/${user._id}/links`), adminApi().get(`/adminUser/users/${user._id}/analytics`)]);
      setUserLinks(lr.data.links); setUserAnalytics(ar.data);
    } catch {}
  };

  const sendEmailBlast = async () => {
    if (!emailForm.subject || !emailForm.body) return;
    setEmailSending(true);
    try { const r = await adminApi().post('/adminUser/email-blast', emailForm); setEmailResult(`✅ Sent to ${r.data.sent} users!`); }
    catch { setEmailResult('❌ Error sending emails'); }
    finally { setEmailSending(false); }
  };

  const fmt = (d: string) => d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 8 }}>my<span style={{ color: '#C0392B' }}>linx</span></div>
        <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading admin panel...</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f9fafb', fontFamily: 'Inter, system-ui, sans-serif', color: '#111' }}>

      {/* ── SIDEBAR ── */}
      <aside style={{
        width: 240, background: '#fff', borderRight: '1px solid #e5e7eb',
        display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 20,
      }}>
        {/* Logo */}
        <div style={{ padding: '24px 20px 20px', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.5 }}>
            my<span style={{ color: '#C0392B' }}>linx</span>
          </div>
          <div style={{ fontSize: 10, color: '#9ca3af', letterSpacing: 2, fontWeight: 600, marginTop: 2 }}>ADMIN PANEL</div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 12px' }}>
          {SIDEBAR_ITEMS.map(item => (
            <button key={item.id} onClick={() => setTab(item.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                width: '100%', padding: '10px 12px', borderRadius: 8,
                border: 'none', cursor: 'pointer', marginBottom: 2,
                background: tab === item.id ? '#fef2f2' : 'transparent',
                color: tab === item.id ? '#C0392B' : '#6b7280',
                fontWeight: tab === item.id ? 700 : 500,
                fontSize: 13, textAlign: 'left',
                transition: 'all 0.15s',
              }}>
              <span style={{ fontSize: 16 }}>{item.icon}</span>
              {item.label}
              {tab === item.id && <div style={{ marginLeft: 'auto', width: 4, height: 4, borderRadius: '50%', background: '#C0392B' }} />}
            </button>
          ))}
        </nav>

        {/* Stats quick view */}
        {stats && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid #f3f4f6', margin: '0 12px 12px', background: '#fef2f2', borderRadius: 10 }}>
            <div style={{ fontSize: 10, color: '#9ca3af', letterSpacing: 1, marginBottom: 8, fontWeight: 600 }}>QUICK STATS</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <div><div style={{ fontWeight: 700, color: '#111' }}>{stats.totalUsers}</div><div style={{ color: '#9ca3af' }}>Users</div></div>
              <div><div style={{ fontWeight: 700, color: '#f59e0b' }}>{stats.premiumUsers}</div><div style={{ color: '#9ca3af' }}>Pro</div></div>
              <div><div style={{ fontWeight: 700, color: '#10b981' }}>{stats.newLast7}</div><div style={{ color: '#9ca3af' }}>New</div></div>
            </div>
          </div>
        )}
      </aside>

      {/* ── MAIN ── */}
      <div style={{ marginLeft: 240, flex: 1, display: 'flex', flexDirection: 'column' }}>

        {/* Top bar */}
        <header style={{
          background: '#fff', borderBottom: '1px solid #e5e7eb',
          padding: '0 32px', height: 60,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 10,
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              {SIDEBAR_ITEMS.find(i => i.id === tab)?.icon} {SIDEBAR_ITEMS.find(i => i.id === tab)?.label}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {actionMsg && (
              <div style={{ background: '#dcfce7', color: '#15803d', padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600 }}>
                {actionMsg}
              </div>
            )}
            <button onClick={logout} style={{
              padding: '8px 16px', borderRadius: 8,
              border: '1px solid #e5e7eb', background: '#fff',
              color: '#6b7280', cursor: 'pointer', fontSize: 12, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              Logout →
            </button>
          </div>
        </header>

        {/* Content */}
        <main style={{ padding: 32, flex: 1 }}>

          {/* ── OVERVIEW ── */}
          {tab === 'overview' && stats && (
            <>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 28 }}>
                <StatCard label="Total Users"   value={stats.totalUsers}   icon="👥" color="#111" />
                <StatCard label="New This Week" value={stats.newLast7}     icon="📈" color="#10b981" sub={`+${stats.newLast30} this month`} />
                <StatCard label="Pro Users"     value={stats.premiumUsers} icon="⭐" color="#f59e0b" />
                <StatCard label="Active Today"  value={stats.activeToday}  icon="🟢" color="#6366f1" />
              </div>

              {/* Funnel */}
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 20 }}>💳 Payment Funnel</div>
                <div style={{ display: 'flex', gap: 0, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Pricing Visits', value: stats.funnel.pricingVisits, color: '#6366f1', icon: '👀' },
                    { label: 'Payment Success', value: stats.funnel.paymentSuccess, color: '#10b981', icon: '✅' },
                    { label: 'Payment Failed', value: stats.funnel.paymentFailed, color: '#ef4444', icon: '❌' },
                    { label: 'Cancelled', value: stats.funnel.paymentCancelled, color: '#9ca3af', icon: '↩️' },
                    { label: 'Conversion Rate', value: `${stats.funnel.conversionRate}%`, color: '#f59e0b', icon: '🎯' },
                  ].map((s, i) => (
                    <div key={i} style={{ flex: 1, minWidth: 120, padding: '12px 20px', borderRight: i < 4 ? '1px solid #f3f4f6' : 'none' }}>
                      <div style={{ fontSize: 20, marginBottom: 6 }}>{s.icon}</div>
                      <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Signup chart */}
              {stats.signupTrend?.length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 20 }}>📈 Signups — Last 7 Days</div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', height: 80 }}>
                    {stats.signupTrend.map((d: any, i: number) => {
                      const max = Math.max(...stats.signupTrend.map((x: any) => x.count));
                      const h = max > 0 ? (d.count / max) * 80 : 4;
                      return (
                        <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: 1 }}>
                          <div style={{ fontSize: 11, color: '#C0392B', fontWeight: 700 }}>{d.count}</div>
                          <div style={{ width: '100%', height: h, background: 'linear-gradient(180deg, #C0392B, #e8763a)', borderRadius: 4, minHeight: 4 }} />
                          <div style={{ fontSize: 10, color: '#9ca3af' }}>{d._id.slice(5)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── USERS ── */}
          {tab === 'users' && (
            <>
              <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                <input type="text" placeholder="🔍 Search by name, email or username..."
                  value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                  style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#111', fontSize: 13, outline: 'none', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
                />
              </div>

              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      {['Name', 'Email', 'Username', 'Country', 'Joined', 'Last Login', 'Status', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#6b7280', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u, i) => (
                      <tr key={u._id} style={{ borderBottom: '1px solid #f3f4f6', background: u.isBanned ? '#fef2f2' : 'transparent' }}>
                        <td style={{ padding: '14px 16px', fontWeight: 600, color: u.isBanned ? '#ef4444' : '#111' }}>
                          {u.name} {u.isBanned && <span style={{ fontSize: 10, background: '#fee2e2', color: '#ef4444', padding: '2px 6px', borderRadius: 4 }}>BANNED</span>}
                        </td>
                        <td style={{ padding: '14px 16px', color: '#6b7280', fontSize: 12 }}>{u.email}</td>
                        <td style={{ padding: '14px 16px', color: '#C0392B', fontWeight: 600 }}>@{u.username || '—'}</td>
                        <td style={{ padding: '14px 16px', color: '#6b7280', fontSize: 12 }}>{u.signupCountry || '—'}</td>
                        <td style={{ padding: '14px 16px', color: '#9ca3af', fontSize: 12 }}>{fmtDate(u.createdAt)}</td>
                        <td style={{ padding: '14px 16px', color: '#9ca3af', fontSize: 12 }}>{fmt(u.lastLogin)}</td>
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{
                            padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                            background: u.isPremium ? '#fef3c7' : '#f3f4f6',
                            color: u.isPremium ? '#d97706' : '#9ca3af',
                          }}>
                            {u.isPremium ? '⭐ PRO' : 'FREE'}
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => togglePremium(u._id)} style={{
                              padding: '5px 10px', borderRadius: 6, border: '1px solid', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                              borderColor: u.isPremium ? '#e5e7eb' : '#fde68a',
                              background: u.isPremium ? '#f9fafb' : '#fffbeb', color: u.isPremium ? '#9ca3af' : '#d97706',
                            }}>{u.isPremium ? 'Revoke' : '⭐ PRO'}</button>
                            <button onClick={() => toggleBan(u._id)} style={{
                              padding: '5px 10px', borderRadius: 6, border: '1px solid', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                              borderColor: u.isBanned ? '#bbf7d0' : '#fecaca',
                              background: u.isBanned ? '#f0fdf4' : '#fef2f2', color: u.isBanned ? '#16a34a' : '#dc2626',
                            }}>{u.isBanned ? 'Unban' : 'Ban'}</button>
                            <button onClick={() => openUserModal(u)} style={{
                              padding: '5px 10px', borderRadius: 6, border: '1px solid #dbeafe', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                              background: '#eff6ff', color: '#2563eb',
                            }}>View</button>
                            {isTrialExpired(u) && (
                              <button onClick={() => sendTrialMail(u._id, u.email)} style={{
                                padding: '5px 10px', borderRadius: 6, border: '1px solid #fed7aa', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                                background: '#fff7ed', color: '#ea580c',
                              }}>📧 Trial Mail</button>
                            )}
                            <button onClick={() => deleteUser(u._id, u.name)} style={{
                              padding: '5px 10px', borderRadius: 6, border: '1px solid #fecaca', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                              background: '#fef2f2', color: '#dc2626',
                            }}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'center', alignItems: 'center' }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', cursor: 'pointer', opacity: page === 1 ? 0.4 : 1 }}>← Prev</button>
                <span style={{ padding: '7px 16px', color: '#9ca3af', fontSize: 12, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>Page {page} of {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', cursor: 'pointer', opacity: page === totalPages ? 0.4 : 1 }}>Next →</button>
              </div>
            </>
          )}

          {/* ── FUNNEL ── */}
          {tab === 'funnel' && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', fontSize: 13, fontWeight: 700 }}>Payment Funnel Events — Last 100</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    {['Time', 'User', 'Email', 'Event', 'Plan', 'Amount'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#6b7280', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {funnel.map((f, i) => (
                    <tr key={f._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '14px 16px', color: '#9ca3af', fontSize: 12 }}>{fmt(f.createdAt)}</td>
                      <td style={{ padding: '14px 16px', fontWeight: 600 }}>{f.userId?.name || '—'}</td>
                      <td style={{ padding: '14px 16px', color: '#6b7280', fontSize: 12 }}>{f.userId?.email || '—'}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${EVENT_COLORS[f.event]}18`, color: EVENT_COLORS[f.event] }}>
                          {f.event.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', color: '#6b7280' }}>{f.plan || '—'}</td>
                      <td style={{ padding: '14px 16px', color: '#10b981', fontWeight: 700 }}>{f.amount ? `₹${f.amount}` : '—'}</td>
                    </tr>
                  ))}
                  {funnel.length === 0 && <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No events yet</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {/* ── REVENUE ── */}
          {tab === 'revenue' && revenue && (
            <>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 28 }}>
                <StatCard label="Total Revenue"  value={`₹${revenue.totalRevenue.toLocaleString()}`} icon="💰" color="#10b981" />
                <StatCard label="Monthly Users"  value={revenue.monthlyUsers}  icon="📅" color="#6366f1" />
                <StatCard label="Yearly Users"   value={revenue.yearlyUsers}   icon="🗓" color="#a855f7" />
                <StatCard label="Total Premium"  value={revenue.totalPremium}  icon="⭐" color="#f59e0b" />
              </div>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', fontSize: 13, fontWeight: 700 }}>Recent Payments</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      {['Time', 'User', 'Email', 'Amount'].map(h => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#6b7280', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {revenue.recentPayments.map((p: any, i: number) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '14px 16px', color: '#9ca3af', fontSize: 12 }}>{fmt(p.createdAt)}</td>
                        <td style={{ padding: '14px 16px', fontWeight: 600 }}>{p.userId?.name || '—'}</td>
                        <td style={{ padding: '14px 16px', color: '#6b7280', fontSize: 12 }}>{p.userId?.email || '—'}</td>
                        <td style={{ padding: '14px 16px', color: '#10b981', fontWeight: 800, fontSize: 15 }}>₹{p.amount || 0}</td>
                      </tr>
                    ))}
                    {revenue.recentPayments.length === 0 && <tr><td colSpan={4} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No payments yet</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ── EMAIL BLAST ── */}
          {tab === 'email' && (
            <div style={{ maxWidth: 640 }}>
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 28, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 24 }}>🚀 Send Email to Users</div>

                <div style={{ marginBottom: 18 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', letterSpacing: 1, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Target Audience</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[{ value: 'all', label: '🌍 All Users' }, { value: 'premium', label: '⭐ PRO Only' }, { value: 'free', label: '🆓 Free Only' }].map(t => (
                      <button key={t.value} onClick={() => setEmailForm({ ...emailForm, target: t.value })}
                        style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid', cursor: 'pointer', fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
                          borderColor: emailForm.target === t.value ? '#C0392B' : '#e5e7eb',
                          background: emailForm.target === t.value ? '#fef2f2' : '#fff',
                          color: emailForm.target === t.value ? '#C0392B' : '#6b7280' }}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: 18 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', letterSpacing: 1, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Subject</label>
                  <input type="text" placeholder="Email subject line..." value={emailForm.subject}
                    onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#111', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ marginBottom: 24 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', letterSpacing: 1, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Body</label>
                  <textarea placeholder="Write your message here..." value={emailForm.body}
                    onChange={e => setEmailForm({ ...emailForm, body: e.target.value })}
                    rows={10} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#111', fontSize: 13, outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                  />
                </div>

                {emailResult && <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 8, background: emailResult.startsWith('✅') ? '#f0fdf4' : '#fef2f2', color: emailResult.startsWith('✅') ? '#16a34a' : '#dc2626', fontSize: 13, fontWeight: 600 }}>{emailResult}</div>}

                <button onClick={sendEmailBlast} disabled={emailSending || !emailForm.subject || !emailForm.body}
                  style={{ padding: '11px 28px', borderRadius: 8, border: 'none', background: '#C0392B', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: emailSending ? 0.7 : 1 }}>
                  {emailSending ? 'Sending...' : '🚀 Send Email Blast'}
                </button>
              </div>
            </div>
          )}

          {/* ── AUTH ERRORS ── */}
          {tab === 'errors' && (
            <>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
                {[
                  { type: '',                   label: 'All Errors',         color: '#ef4444', icon: '🚨' },
                  { type: 'login_failed',       label: 'Login Failed',       color: '#f59e0b', icon: '🔑' },
                  { type: 'google_auth_failed', label: 'Google Auth',        color: '#6366f1', icon: 'G' },
                  { type: 'register_failed',    label: 'Register Failed',    color: '#a855f7', icon: '📝' },
                  { type: 'otp_failed',         label: 'OTP Failed',         color: '#10b981', icon: '🔢' },
                ].map(({ type, label, color, icon }) => {
                  const count = type === '' ? authSummary.reduce((a, b) => a + b.count, 0) : authSummary.find(s => s._id === type)?.count || 0;
                  return (
                    <div key={type} onClick={() => setErrorFilter(type)} style={{
                      background: errorFilter === type ? '#fff' : '#fff',
                      border: `1.5px solid ${errorFilter === type ? color : '#e5e7eb'}`,
                      borderRadius: 10, padding: '14px 20px', cursor: 'pointer', minWidth: 130,
                      boxShadow: errorFilter === type ? `0 0 0 3px ${color}22` : '0 1px 3px rgba(0,0,0,0.06)',
                      transition: 'all 0.15s',
                    }}>
                      <div style={{ fontSize: 20, marginBottom: 6 }}>{icon}</div>
                      <div style={{ fontSize: 26, fontWeight: 800, color }}>{count}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{label}</div>
                    </div>
                  );
                })}
              </div>

              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      {['Time', 'Type', 'Email', 'Reason', 'IP'].map(h => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#6b7280', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {authErrors.map((e, i) => (
                      <tr key={e._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '14px 16px', color: '#9ca3af', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {new Date(e.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${TYPE_COLORS[e.type] || '#ef4444'}18`, color: TYPE_COLORS[e.type] || '#ef4444' }}>
                            {e.type.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px', color: '#6b7280', fontSize: 12 }}>{e.email || '—'}</td>
                        <td style={{ padding: '14px 16px', color: '#ef4444', fontSize: 12 }}>{e.reason}</td>
                        <td style={{ padding: '14px 16px', color: '#9ca3af', fontSize: 11, fontFamily: 'monospace' }}>{e.ip || '—'}</td>
                      </tr>
                    ))}
                    {authErrors.length === 0 && (
                      <tr><td colSpan={5} style={{ padding: 48, textAlign: 'center' }}>
                        <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                        <div style={{ color: '#9ca3af', fontSize: 13 }}>No auth errors — everything looks clean!</div>
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

        </main>
      </div>

      {/* ── USER DETAIL MODAL ── */}
      {selectedUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}
          onClick={() => setSelectedUser(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 680, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}
            onClick={e => e.stopPropagation()}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{selectedUser.name}</div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{selectedUser.email} · @{selectedUser.username}</div>
              </div>
              <button onClick={() => setSelectedUser(null)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 16, color: '#6b7280' }}>✕</button>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {(['links', 'analytics'] as const).map(t => (
                <button key={t} onClick={() => setUserModalTab(t)} style={{
                  padding: '7px 18px', borderRadius: 8, border: '1px solid', cursor: 'pointer', fontSize: 12, fontWeight: 600, textTransform: 'capitalize',
                  borderColor: userModalTab === t ? '#C0392B' : '#e5e7eb',
                  background: userModalTab === t ? '#fef2f2' : '#fff',
                  color: userModalTab === t ? '#C0392B' : '#6b7280',
                }}>{t}</button>
              ))}
            </div>

            {userModalTab === 'links' && (
              userLinks.length === 0 ? <div style={{ color: '#9ca3af', textAlign: 'center', padding: 32 }}>No links yet</div>
              : userLinks.map((link: any) => (
                <div key={link._id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{link.title}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{link.url}</div>
                  </div>
                  <div style={{ fontSize: 12, color: '#C0392B', fontWeight: 700 }}>👆 {link.clicks || 0}</div>
                  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: link.isActive ? '#f0fdf4' : '#f3f4f6', color: link.isActive ? '#16a34a' : '#9ca3af' }}>
                    {link.isActive ? 'Active' : 'Hidden'}
                  </span>
                </div>
              ))
            )}

            {userModalTab === 'analytics' && userAnalytics && (
              <div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                  {[
                    { label: 'Top Country', value: userAnalytics.countries?.[0]?._id || '—', icon: '🌍' },
                    { label: 'Top Device',  value: userAnalytics.devices?.[0]?._id  || '—', icon: '📱' },
                    { label: 'Clicks (30d)', value: userAnalytics.clickTrend?.reduce((a: number, b: any) => a + b.count, 0) || 0, icon: '👆' },
                  ].map((s, i) => (
                    <div key={i} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 18px', flex: 1, minWidth: 120 }}>
                      <div style={{ fontSize: 18, marginBottom: 4 }}>{s.icon}</div>
                      <div style={{ fontSize: 18, fontWeight: 800 }}>{s.value}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                {userAnalytics.topLinks?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, color: '#9ca3af', letterSpacing: 1, marginBottom: 10, fontWeight: 600, textTransform: 'uppercase' }}>Top Links (30 days)</div>
                    {userAnalytics.topLinks.map((l: any, i: number) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                        <span style={{ fontWeight: 500 }}>{l.title || 'Unknown'}</span>
                        <span style={{ color: '#C0392B', fontWeight: 700 }}>👆 {l.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;